import type { VideoAssignment, VideoTaskStatus } from '@platform/shared-types';

// Shared display helpers for the Video tasks tab and its Review screen.
// Tones follow the approved mockup (SA-Assign / SA-Review): ruby for
// "waiting on you", amber for "in flight", emerald for done. No purple.

export const STATUS_LABEL: Record<VideoTaskStatus, string> = {
  draft: 'Draft',
  assigned: 'Assigned',
  in_progress: 'Recording',
  submitted: 'Submitted',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  cancelled: 'Cancelled',
};

export const STATUS_TONE: Record<VideoTaskStatus, string> = {
  draft: 'bg-gray-100 text-gray-500',
  assigned: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-amber-100 text-amber-800',
  submitted: 'bg-[#FFF1F3] text-[#9F1239]',
  changes_requested: 'bg-orange-100 text-orange-800',
  approved: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-50 text-red-500',
};

/** Filter pills, in mockup order. `null` = All. */
export const STATUS_FILTERS: Array<{ id: VideoTaskStatus | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'in_progress', label: 'Recording' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'changes_requested', label: 'Changes requested' },
  { id: 'approved', label: 'Approved' },
];

export const SURFACE_LABEL: Record<VideoAssignment['surface'], string> = {
  'platform-app': 'Platform app',
  pos: 'POS web till',
};

const OPEN = new Set<VideoTaskStatus>(['draft', 'assigned', 'in_progress', 'changes_requested']);

/** Same rule as the backend's `counts.overdue`: open, not waiting on review, past due. */
export const isOverdue = (a: Pick<VideoAssignment, 'status' | 'dueAt'>, now = Date.now()) =>
  OPEN.has(a.status) && !!a.dueAt && a.dueAt < now;

/** "Mon 6 Oct" */
export const formatDue = (ms?: number) =>
  (ms ? new Date(ms).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');

/** 125000 -> "2:05" */
export const mmss = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** 522000 -> "8m 42s" */
export const formatDuration = (ms: number) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  return m ? `${m}m ${total % 60}s` : `${total}s`;
};

/** DatePicker's YYYY-MM-DD <-> an epoch-ms due time at the END of that local day. */
export const dueFromDate = (ymd: string): number | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return undefined;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59).getTime();
};
export const dateFromDue = (ms?: number): string => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Original-voice renders carry a `transcribe:` / `clean:` note in
 * render.error when STT or cleanup failed but the chain carried on -- the
 * video is still fine, only the transcript/script is missing.
 */
export const isTranscriptNote = (err?: string | null) => !!err && /^(transcribe|clean):/.test(err);
